import { ThemeProvider } from "@emotion/react";
import { EditComponentProps } from "@material-table/core";
import {
  DesktopDatePicker,
  DesktopDatePickerProps,
  LocalizationProvider,
} from "@mui/lab";
import AdapterDayjs from "@mui/lab/AdapterDayjs";
import { createTheme, TextField, TextFieldProps } from "@mui/material";
import { SInputProps } from "components/parts";
import { STheme } from "components/styles";
import ja from "dayjs/locale/ja";
import { useState } from "react";
import { dateAt, validDate } from "services";

type Props = Partial<Omit<TextFieldProps, "value" | "type">> &
  Pick<DesktopDatePickerProps, "onAccept"> &
  Pick<SInputProps, "align"> & {
    editProps: EditComponentProps<any>;
    value: any;
  };

export const SDateCell = ({
  value,
  defaultValue,
  align,
  editProps,
  onAccept,
  ...props
}: Props): JSX.Element => {
  const [inputValue, setInputValue] = useState<Date | string>(value);

  const theme = createTheme(
    {
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 0,
              paddingRight: "3px",
              backgroundColor: STheme.fields.normal,
              borderColor: STheme.fields.border,
              "&.Mui-disabled": {
                color: STheme.fields.text,
                backgroundColor: STheme.fields.disabled,
              },
            },
            input: {
              textAlign: align,
              fontSize: "1rem",
              paddingLeft: "4px",
              paddingRight: "0px",
              paddingTop: "1px",
              paddingBottom: "0px",
              color: STheme.fields.text,
              "&.Mui-disabled": {
                fontSize: "0.9rem",
                WebkitTextFillColor: STheme.fields.text,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              margin: 0,
            },
          },
        },
      },
    },
    { ...STheme }
  );

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} locale={ja}>
        <DesktopDatePicker
          value={inputValue}
          disabled={props.disabled}
          onAccept={(date) => {
            const dateString = getDateString(date, undefined);
            editProps.onChange(dateString);
            setInputValue(dateString);
            onAccept?.call(null, dateString);
          }}
          onChange={(date, value) => {
            const dateString = getDateString(date, value);
            editProps.onChange(dateString);
            setInputValue(dateString);
          }}
          mask="____/__/__"
          renderInput={({ inputRef, inputProps, InputProps, onChange }) => (
            <TextField
              {...props}
              value={value}
              defaultValue={defaultValue}
              inputRef={inputRef}
              onBlur={(event) => {
                props.onBlur?.call(this, event);
                editProps.onChange(event.currentTarget.value);
              }}
              onChange={(event) => {
                onChange?.call(this, event);
                props.onChange?.call(this, event);
              }}
              InputProps={props.disabled ? undefined : InputProps}
              inputProps={{ ...inputProps, placeholder: "", tabIndex: 0 }}
              autoComplete="off"
              fullWidth
            />
          )}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const getDateString = (date: any, value: string | undefined): string => {
  if (typeof value !== "undefined" && validDate(value)) return dateAt(value);
  if (date === null) return "";
  if (validDate(date)) return dateAt(date);
  return "Invalid Date";
};
